(() => {
  const initialized = new WeakSet();

  function initializePreviewDialog(root) {
    const dialog = root.querySelector?.('[data-mozi-preview-dialog]') || document.querySelector('[data-mozi-preview-dialog]');
    if (!dialog || initialized.has(dialog)) return;
    initialized.add(dialog);

    const openers = document.querySelectorAll('[data-mozi-preview-open]');
    const close = dialog.querySelector('[data-mozi-preview-close]');
    let opener = null;

    for (const button of openers) {
      if (initialized.has(button)) continue;
      initialized.add(button);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        opener = button;
        dialog.showModal();
        dialog.querySelector('input[type="email"]')?.focus() || close?.focus();
      });
    }

    close?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => opener?.focus());
    if (dialog.querySelector('[data-mozi-waitlist-success]')) {
      dialog.showModal();
      close?.focus();
    }
  }

  function initializeJournalRail(root) {
    const rails = root.querySelectorAll?.('[data-journal-rail]') || [];
    for (const rail of rails) {
      if (initialized.has(rail)) continue;
      initialized.add(rail);
      rail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const card = rail.querySelector('.oh-benefit');
        if (!card) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + 24), behavior });
      });
    }
  }

  function closeMobileMenuAfterNavigation(root) {
    const menus = root.querySelectorAll?.('.mozi-mobile-menu') || [];
    for (const menu of menus) {
      if (initialized.has(menu)) continue;
      initialized.add(menu);
      for (const link of menu.querySelectorAll('a, button[type="submit"]')) {
        link.addEventListener('click', () => menu.removeAttribute('open'));
      }
      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        menu.removeAttribute('open');
        menu.querySelector('summary')?.focus();
      });
    }
  }

  function initializeCampaignMarquee(root) {
    const marquees = root.matches?.('.mozi-campaign-marquee')
      ? [root]
      : root.querySelectorAll?.('.mozi-campaign-marquee') || [];

    for (const marquee of marquees) {
      if (initialized.has(marquee)) continue;
      initialized.add(marquee);

      const load = () => {
        const images = marquee.querySelectorAll('.mozi-campaign-marquee__set:first-child img');
        const ready = [...images].map((image) => new Promise((resolve) => {
          image.loading = 'eager';
          if (image.complete && image.naturalWidth > 0) {
            resolve();
            return;
          }
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }));
        Promise.all(ready).then(() => marquee.dataset.campaignReady = 'true');
      };

      if (!('IntersectionObserver' in window)) {
        load();
        continue;
      }

      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        load();
      }, { rootMargin: '600px 0px' });
      observer.observe(marquee);
    }
  }

  function initializePurchaseForms(root) {
    const forms = root.matches?.('[data-mozi-purchase-form]')
      ? [root]
      : root.querySelectorAll?.('[data-mozi-purchase-form]') || [];

    for (const form of forms) {
      if (initialized.has(form)) continue;
      initialized.add(form);

      const button = form.querySelector('.mozi-purchase-button');
      const error = form.querySelector('[data-purchase-error]');
      if (!button) continue;
      const label = button.textContent;
      const purchaseAvailable = !button.disabled;
      const reset = () => {
        button.disabled = !purchaseAvailable;
        button.textContent = label;
        button.removeAttribute('aria-busy');
      };
      window.addEventListener('pageshow', reset);

      form.addEventListener('submit', async (event) => {
        if (button.disabled || !form.dataset.cartClearUrl || !form.dataset.cartAddUrl || !form.dataset.checkoutUrl) return;

        event.preventDefault();
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingLabel || label;
        if (error) error.hidden = true;

        try {
          const membershipVariantId = Number(form.dataset.membershipVariantId);
          const membershipSellingPlanId = Number(form.dataset.membershipSellingPlanId);
          const scannerVariantId = Number(form.dataset.scannerVariantId);
          const warrantyVariantId = Number(form.dataset.warrantyVariantId);
          if (![membershipVariantId, membershipSellingPlanId, scannerVariantId, warrantyVariantId].every(Number.isSafeInteger)) {
            throw new Error('purchase-configuration-invalid');
          }
          const clearResponse = await fetch(form.dataset.cartClearUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          if (!clearResponse.ok) throw new Error('cart-clear-failed');

          const response = await fetch(form.dataset.cartAddUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              items: [
                { id: warrantyVariantId, quantity: 1 },
                { id: scannerVariantId, quantity: 1 },
                { id: membershipVariantId, quantity: 1, selling_plan: membershipSellingPlanId }
              ]
            })
          });
          if (!response.ok) throw new Error('cart-add-failed');
          const cartAdd = await response.json();
          if (!Array.isArray(cartAdd.items) || cartAdd.items.length !== 3) throw new Error('cart-bundle-incomplete');
          location.assign(form.dataset.checkoutUrl);
        } catch (_error) {
          reset();
          if (error) error.hidden = false;
        }
      });
    }
  }

  function initializeLowPriorityPreload() {
    if (initialized.has(document.documentElement)) return;
    initialized.add(document.documentElement);
    if (navigator.connection?.saveData) return;

    const preload = () => {
      for (const image of document.querySelectorAll('img[loading="lazy"]')) {
        if (image.getBoundingClientRect().top > window.innerHeight * 2) continue;
        image.loading = 'eager';
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload, { timeout: 1500 });
    } else {
      window.setTimeout(preload, 750);
    }
  }

  function initializeHeaderState(root) {
    const header = root.matches?.('[data-mozi-header]')
      ? root
      : root.querySelector?.('[data-mozi-header]') || document.querySelector('[data-mozi-header]');
    if (!header) return;

    const update = () => header.classList.toggle('is-scrolled', scrollY > 24);
    update();
    if (initialized.has(header)) return;
    initialized.add(header);
    addEventListener('scroll', update, { passive: true });
  }

  function initializeRegionSuggestion(root) {
    const prompt = root.querySelector?.('[data-mozi-region-suggestion]') || document.querySelector('[data-mozi-region-suggestion]');
    if (!prompt || initialized.has(prompt) || !window.Shopify?.routes?.root) return;
    initialized.add(prompt);

    const currentCountry = prompt.dataset.currentCountry;
    const countryName = prompt.querySelector('[data-mozi-region-name]');
    const countryCode = prompt.querySelector('[data-mozi-region-code]');
    const accept = prompt.querySelector('[data-mozi-region-accept]');
    const dismiss = prompt.querySelector('[data-mozi-region-dismiss]');
    const storageKey = 'mozi:region-suggestion:v1';

    try {
      if (sessionStorage.getItem(storageKey)) return;
    } catch (_error) {
    }

    const close = (value) => {
      prompt.hidden = true;
      try {
        sessionStorage.setItem(storageKey, value);
      } catch (_error) {
      }
    };

    dismiss?.addEventListener('click', () => close('dismissed'));
    accept?.addEventListener('click', () => {
      try {
        sessionStorage.setItem(storageKey, 'accepted');
      } catch (_error) {
      }
    });

    const endpoint = new URL(`${window.Shopify.routes.root}browsing_context_suggestions.json`, location.origin);
    endpoint.searchParams.set('country[enabled]', 'true');
    endpoint.searchParams.set('country[exclude]', currentCountry);

    fetch(endpoint, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        const suggestion = data?.suggestions?.find((entry) => entry?.parts?.country)?.parts?.country;
        const handle = suggestion?.handle?.toUpperCase();
        if (!handle || !/^[A-Z]{2}$/.test(handle) || handle === currentCountry || !suggestion.name) return;
        countryName.textContent = suggestion.name;
        countryCode.value = handle;
        prompt.hidden = false;
      })
      .catch(() => {});
  }

  function initializePrivacyPolicyLink() {
    const policyUrl = document.querySelector('[data-mozi-policy-url]')?.href;
    const bannerPolicyLink = document.querySelector('#shopify-pc__banner__body-policy-link');
    if (!policyUrl || !bannerPolicyLink) return;
    bannerPolicyLink.href = policyUrl;
  }

  function initialize(root = document) {
    initializeHeaderState(root);
    initializePreviewDialog(root);
    initializeJournalRail(root);
    initializeCampaignMarquee(root);
    initializePurchaseForms(root);
    closeMobileMenuAfterNavigation(root);
    initializeLowPriorityPreload();
    initializeRegionSuggestion(root);
    initializePrivacyPolicyLink();
  }

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
